import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Lightweight hook for fetching unread events count
 * Used by sidebar navigation for badge display
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable fetching (default: true)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 60000)
 * @returns {Object} { count, loading, refresh }
 */
export function useUnreadCount(options = {}) {
  const {
    enabled = true,
    refreshInterval = 60000, // 60 seconds
  } = options;

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch unread count from API
   */
  const fetchCount = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/logs/unread-count`, {
        withCredentials: true,
      });
      setCount(response.data.count || 0);
    } catch (err) {
      // Silently fail - don't break navigation for count fetch errors
      console.error('Error fetching unread count:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  /**
   * Manual refresh
   */
  const refresh = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  // Initial fetch on mount
  useEffect(() => {
    if (enabled) {
      fetchCount();
    }
  }, [enabled, fetchCount]);

  // Auto-refresh interval
  useEffect(() => {
    if (!enabled || !refreshInterval) return;

    const interval = setInterval(() => {
      fetchCount();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enabled, refreshInterval, fetchCount]);

  return {
    count,
    loading,
    refresh,
  };
}

export default useUnreadCount;
